import * as React from 'react';

import { Application } from '@mam/main/Application';
import Layout from '~/Layout';
import NavigationMenu from '~/UI/NavigationMenu';
import { NotificationsFeedContainer } from '@mam/notifications-feed/src/StyleNotificationsFeedContainer';
import { NotificationEmailDetail } from '@mam/main/Application/Screens/NotificationEmailDetail';
import { Panel as UserImpersonatePanel } from '~/UserImpersonatePanel';
import { Route } from 'react-router-dom';
import { UserNavigationItem } from '~/UserNavigationItem';

import { useDispatch, useSelector, useStore } from 'react-redux';

import { checkedFetchAccessTokenIfNeeded } from '~/AccessToken';
import { logError } from '~/Logger';

import defaultApplicationServiceFactory from '@mam/main/Application/defaultApplicationServiceFactory';

import { AppError, handleError } from '~/ErrorHandler';

import type { ServiceFactory } from '@mam/main/Application/Application';
import type { ApplicationState, ThunkDispatch } from '~/Store';

const NotificationsCenterEntry: React.FC<React.PropsWithChildren<Record<never, never>>> = () => {
	const store = useStore<ApplicationState>();
	// TODO: Add correct type
	const dispatch = useDispatch<ThunkDispatch<any>>();
	const accessToken = useSelector((state: ApplicationState) => state.accessToken.accessToken);

	React.useEffect(() => {
		// NOTE: check if user is logged in or not, triggers login redirect if needed
		dispatch(checkedFetchAccessTokenIfNeeded());
	});

	const portedServiceFactory: ServiceFactory = {
		...defaultApplicationServiceFactory,
		createErrorHandler: () => ({
			handle(error: AppError) {
				logError(error);
				dispatch(handleError(error));
				return null;
			},
		}),
		createAccessTokenStorage: () => ({
			load() {
				return store.getState().accessToken.accessToken?.token || null;
			},
			remove() {},
			save() {},
		}),
	};

	if (!accessToken) {
		return null;
	}

	return (
		<Application
			apiBaseUrl={process.env.API_BASE_URL ?? '/api/v1/'}
			errorLogger={{ logError }}
			pigeonAuthProvider={process.env.PIGEON_AUTH_PROVIDER ?? 'app-pm@cogvio.com'}
			pigeonBaseUrl={process.env.PIGEON_BASE_URL ?? 'https://notifications-api.cogvio.com/api/v1/'}
			serviceFactory={portedServiceFactory}
		>
			<Route
				exact
				path='/notifications/:section/:subsection'
				render={() => (
					<div style={{ overflow: 'scroll', height: '100%' }}>
						<NotificationEmailDetail />
					</div>
				)}
			/>
			<Route
				exact
				path='/notifications'
				render={() => (
					<Layout
						bottomNavigationPanel={<UserImpersonatePanel />}
						navigation={<NavigationMenu />}
						navigationPanel={<UserNavigationItem />}
					>
						{/* NOTE: fontSize: initial - override font-size from reset.less for tailwind */}
						<section style={{ fontSize: 'initial', overflow: 'scroll', width: '100%', height: '100%' }}>
							<div
								className='container mx-auto px-10'
								data-testid='notifications-feed-container'
								style={{ maxWidth: 1260 }}
							>
								<NotificationsFeedContainer />
							</div>
						</section>
					</Layout>
				)}
			/>
		</Application>
	);
};

export default React.memo(NotificationsCenterEntry);
